import { native } from '@forest/native-services'
import { cn } from '@forest/shared'
import Dialog from '@corvu/dialog'
import Drawer from '@corvu/drawer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/solid-query'
import { createSignal, Match, Show, Switch } from 'solid-js'
import { Portal } from 'solid-js/web'
import { currencyArt } from '~/assets/rewards'
import { deriveRevealContent, type RevealContent } from '~/lib/delivery'
import { isValidInviteCode, normalizeInviteCode } from '~/lib/invite-code'
import { m } from '~/paraglide/messages'
import {
  RedeemError,
  type RedeemErrorReason,
  redeemInviteCodeAsUser,
  referralQueries,
} from '~/services/referral'
import { userQueries } from '~/services/user'
import { CheckIcon, InfoIcon } from './icons'
import { RewardReveal } from './reward-reveal'
import { Spinner } from './spinner'

type SheetError = 'invalid' | 'network' | RedeemErrorReason

const errorMessage: Record<SheetError, () => string> = {
  invalid: m.invalid_code_message,
  not_exist: m.not_exist_message,
  use_own: m.use_own_code_message,
  already_bound: m.one_code_at_a_time_message,
  network: m.dialog_connection_issue_description,
}

// Solid CTA per Figma Button Style=Primary — shared by the sheet's Confirm
// and the dialogs' buttons.
const ctaClass =
  'flex h-[52px] items-center justify-center rounded-full bg-cta text-base font-bold leading-5 text-white shadow-[0_0_20px_-2px_rgba(198,255,205,0.15)] transition-colors duration-150 active:bg-cta-press'

export function EnterCodeSheet() {
  const queryClient = useQueryClient()
  const binding = useQuery(() => referralQueries.binding())

  const [code, setCode] = createSignal('')
  const [error, setError] = createSignal<SheetError | null>(null)
  const [confirmOpen, setConfirmOpen] = createSignal(false)
  // Post-bind celebration (see CONTEXT.md "Bind reward"): the reveal opens
  // directly on success; the reward is already delivered when it shows.
  const [reveal, setReveal] = createSignal<RevealContent | null>(null)

  const redeem = useMutation(() => ({
    mutationFn: async (code: string) => {
      // Snapshot the pre-claim balance before the bind reward credits it
      // server-side — the reveal's pills count up from this cached value
      // (see userQueries.currency). A failed fetch blocks the redeem (the
      // bind request never fires) and surfaces as the network error below.
      await queryClient.ensureQueryData(userQueries.currency())
      return redeemInviteCodeAsUser(code)
    },
    onSuccess: (delivery, submittedCode) => {
      // Cache the code that was actually bound, not the live signal.
      queryClient.setQueryData(referralQueries.binding().queryKey, {
        bound: true,
        code: submittedCode,
      })
      setReveal(deriveRevealContent(delivery, undefined, currencyArt))
    },
    onError: (err) => {
      setError(err instanceof RedeemError ? err.reason : 'network')
    },
  }))

  const claimed = () => binding.data?.bound === true
  const pending = () => redeem.isPending
  // The dialogs and the reward reveal portal their content outside the
  // drawer, so while one is open (or a redeem is in flight) the drawer must
  // ignore what it would otherwise read as outside interactions and close on.
  const canDismiss = () => !pending() && !confirmOpen() && !reveal()

  const submit = () => {
    if (claimed() || pending()) return
    const normalized = normalizeInviteCode(code())
    if (!isValidInviteCode(normalized)) {
      setError('invalid')
      return
    }
    setCode(normalized)
    setConfirmOpen(true)
  }

  const confirmRedeem = () => {
    setConfirmOpen(false)
    setError(null)
    redeem.mutate(code())
  }

  return (
    <Drawer
      side="bottom"
      onOpenChange={(open) => {
        if (open) {
          void native.general.logEvent({
            event_name: 'sheet_view',
            event_params: { sheet_name: 'enter_c2c_referral_code' },
          })
        }
      }}
      closeOnEscapeKeyDown={canDismiss()}
      closeOnOutsidePointer={canDismiss()}
      closeOnOutsideFocus={false}
      onInitialFocus={(event) => {
        // Default initial focus lands on the input and summons the native
        // keyboard; the claimed state has no input to focus.
        if (claimed()) event.preventDefault()
      }}
    >
      {(drawerProps) => (
        <>
          <Drawer.Trigger
            class="px-2 py-2 text-sm font-bold text-ink [text-shadow:0_0_5.5px_rgba(255,255,255,1)]"
            onClick={() => {
              void native.general.logEvent({
                event_name: 'component_event',
                event_params: {
                  type: 'btn',
                  app_action: 'click',
                  component_name: 'enter_c2c_referral_code',
                },
              })
            }}
          >
            {m.enter_code()}
          </Drawer.Trigger>

          <Drawer.Portal>
            {/* The blur radius is constant and the whole overlay fades via
                opacity — animating backdrop-filter itself re-blurs the page
                every frame, which drops frames in the in-app webview. */}
            <Drawer.Overlay
              class="fixed inset-0 z-30 bg-black/10 backdrop-blur-[12.5px] data-[transitioning]:transition-opacity data-[transitioning]:duration-300 data-[transitioning]:[transition-timing-function:cubic-bezier(0.32,0.72,0,1)]"
              style={{ opacity: drawerProps.openPercentage }}
            />
            <Drawer.Content
              // Figma 322:670923: white/80 + blur(50) sheet, 36px top radius,
              // 2px gradient inner stroke. The after: block covers the gap
              // revealed when the sheet is dragged past its snap point.
              class="gradient-outline fixed inset-x-0 bottom-0 z-30 mx-auto flex w-full max-w-[640px] flex-col items-center gap-6 rounded-t-[36px] bg-white/80 px-6 pt-3 backdrop-blur-[50px] after:absolute after:inset-x-0 after:top-[calc(100%-1px)] after:h-1/2 after:bg-inherit data-[transitioning]:transition-transform data-[transitioning]:duration-300 data-[transitioning]:[transition-timing-function:cubic-bezier(0.32,0.72,0,1)]"
              style={{
                '--ow': '2px',
                'padding-bottom': 'max(var(--safe-area-bottom), 42px)',
              }}
              data-corvu-no-drag={pending() ? '' : undefined}
            >
              <div class="h-1.5 w-[60px] rounded-[40px] bg-black/20" />

              <div class="flex w-full max-w-[331px] flex-col gap-8">
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-1 text-center">
                    <Drawer.Label class="text-lg font-semibold leading-[26px] text-[#333]">
                      {claimed()
                        ? m.invite_code_claimed_title()
                        : m.enter_invite_code_title()}
                    </Drawer.Label>
                    <Drawer.Description class="text-base leading-[22px] text-[#666]">
                      {claimed()
                        ? m.invite_code_claimed_description()
                        : m.enter_invite_code_description()}
                    </Drawer.Description>
                  </div>

                  <Switch>
                    <Match when={claimed()}>
                      <div class="flex h-[52px] items-center justify-between rounded-2xl border border-[#D6D6D6] bg-[#EFEFEF] px-3">
                        <span class="text-base leading-[22px] text-[#888]">
                          {binding.data?.code}
                        </span>
                        <span class="flex size-6 items-center justify-center rounded-full bg-moss">
                          <CheckIcon class="size-3.5 text-white" />
                        </span>
                      </div>
                    </Match>
                    <Match when={!claimed()}>
                      <div class="flex flex-col">
                        <input
                          type="text"
                          value={code()}
                          onInput={(event) => {
                            const upper =
                              event.currentTarget.value.toUpperCase()
                            event.currentTarget.value = upper
                            setCode(upper)
                            setError(null)
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') submit()
                          }}
                          // Placeholder has no Lokalise key yet, per design
                          // sign-off it stays hardcoded.
                          placeholder="e.g. WK8T2N6"
                          maxlength="7"
                          autocapitalize="characters"
                          autocomplete="off"
                          spellcheck={false}
                          enterkeyhint="go"
                          readonly={pending()}
                          class="h-[52px] w-full select-text rounded-2xl border border-[#D6D6D6] bg-transparent px-3 text-base leading-[22px] text-[#333] outline-none placeholder:text-[#D6D6D6]"
                        />
                        <Show when={error()}>
                          {(err) => (
                            <p class="mt-1 px-3 text-sm leading-5 text-[#E84741]">
                              {errorMessage[err()]()}
                            </p>
                          )}
                        </Show>
                      </div>
                    </Match>
                  </Switch>
                </div>

                <Show when={!claimed()}>
                  <div class="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={submit}
                      disabled={pending() || code().length === 0}
                      class={cn(
                        ctaClass,
                        // While the redeem is in flight the button is disabled
                        // but stays fully opaque behind the spinner.
                        !pending() && 'disabled:opacity-50',
                      )}
                    >
                      <Show when={pending()} fallback={m.btn_submit()}>
                        <Spinner class="size-5" />
                      </Show>
                    </button>
                    <div class="flex items-center justify-center gap-1 text-ink">
                      <InfoIcon class="size-[14.4px] shrink-0" />
                      <span class="text-sm leading-5">
                        {m.only_one_code_message()}
                      </span>
                    </div>
                  </div>
                </Show>
              </div>
            </Drawer.Content>
          </Drawer.Portal>

          <Dialog
            role="alertdialog"
            open={confirmOpen()}
            onOpenChange={setConfirmOpen}
          >
            <Dialog.Portal>
              <Dialog.Overlay class="fixed inset-0 z-40 bg-black/10 backdrop-blur-[12.5px] data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0" />
              <Dialog.Content
                // Figma 470:36630: same white/80 + blur(50) material and 2px
                // gradient inner stroke as the sheet, 32px radius.
                class="gradient-outline fixed inset-0 z-40 m-auto flex h-fit w-[300px] flex-col gap-6 rounded-[32px] bg-white/80 p-6 backdrop-blur-[50px] data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 data-[closed]:zoom-out-95 data-[open]:zoom-in-95"
                style={{ '--ow': '2px' }}
              >
                <div class="flex flex-col gap-2 text-center">
                  <Dialog.Label class="text-lg font-semibold leading-[26px] text-[#333]">
                    {m.dialog_confirm_invite_code_title()}
                  </Dialog.Label>
                  <Dialog.Description class="text-base leading-[22px] text-[#666]">
                    {m.dialog_confirm_invite_code_description()}
                  </Dialog.Description>
                </div>
                <div class="flex gap-2">
                  <Dialog.Close
                    aria-label={m.cancel()}
                    class="flex h-[52px] flex-1 items-center justify-center rounded-full text-base font-bold leading-5 text-cta"
                  >
                    {m.cancel()}
                  </Dialog.Close>
                  <button
                    type="button"
                    onClick={confirmRedeem}
                    class={cn(ctaClass, 'flex-1')}
                  >
                    {m.btn_submit()}
                  </button>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog>

          {/* Portaled out of the fixed z-20 top nav — inline, the reveal
              would be trapped under the drawer's portaled overlay. */}
          <Show when={reveal()}>
            {(content) => (
              <Portal>
                <RewardReveal
                  content={content()}
                  onClose={() => setReveal(null)}
                />
              </Portal>
            )}
          </Show>
        </>
      )}
    </Drawer>
  )
}
